import type { PanzoomObject } from '@panzoom/panzoom';

export const MIN_SCALE = 1;
export const MAX_SCALE = 5;
/** Above this the image counts as zoomed: swiping stands down and dragging pans. */
export const ZOOMED_ABOVE = 1.01;
const DOUBLE_TAP_SCALE = 2;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP_PX = 24;
/* Zoom per wheel pixel. Panzoom's own zoomWithWheel takes a fixed step per event whatever its
   size, which is right for a mouse notch but turns a trackpad pinch, dozens of tiny events,
   into a lurch. A pinch reports small deltas with ctrlKey set, hence its larger rate. */
const WHEEL_RATE = 0.002;
const PINCH_RATE = 0.01;
const LINE_HEIGHT_PX = 16;
const DOM_DELTA_LINE = 1;
const DOM_DELTA_PAGE = 2;

export interface ZoomGestureTarget {
    /** The transformed <figure>. */
    node: HTMLElement;
    /** Its parent, which takes the wheel so the zoom controls on it also respond. */
    stage: HTMLElement;
    panzoom: PanzoomObject;
    scale: () => number;
    isDisabled: () => boolean;
}

/**
 * The gestures Panzoom has no built-in handling for: wheel and trackpad pinch, double-click,
 * and double-tap. Returns the function that unbinds them.
 */
export function bindZoomGestures(target: ZoomGestureTarget): () => void {
    const { node, stage, panzoom } = target;

    const onWheel = (event: WheelEvent) => {
        if (target.isDisabled()) {
            return;
        }
        // A sideways trackpad swipe is not a zoom; a pinch arrives as a wheel with ctrlKey.
        if (!event.ctrlKey && Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
            return;
        }
        // Scrolling down over an unzoomed image scrolls the page, as it would anywhere else.
        if (event.deltaY > 0 && target.scale() <= MIN_SCALE) {
            return;
        }
        event.preventDefault();
        const pixels =
            event.deltaMode === DOM_DELTA_LINE
                ? event.deltaY * LINE_HEIGHT_PX
                : event.deltaMode === DOM_DELTA_PAGE
                  ? event.deltaY * stage.clientHeight
                  : event.deltaY;
        const factor = Math.exp(-pixels * (event.ctrlKey ? PINCH_RATE : WHEEL_RATE));
        const toScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, target.scale() * factor));
        panzoom.zoomToPoint(toScale, event);
    };

    const toggleAt = (point: { clientX: number; clientY: number }) => {
        if (target.isDisabled()) {
            return;
        }
        if (target.scale() > ZOOMED_ABOVE) {
            panzoom.reset();
        } else {
            panzoom.zoomToPoint(DOUBLE_TAP_SCALE, point);
        }
    };

    let lastPointerType = 'mouse';
    const touches = new Set<number>();
    let multiTouch = false;
    let lastTap: { time: number; x: number; y: number } | null = null;

    const onPointerDown = (event: PointerEvent) => {
        lastPointerType = event.pointerType;
        if (event.pointerType !== 'touch') {
            return;
        }
        touches.add(event.pointerId);
        multiTouch = multiTouch || touches.size > 1;
    };
    const onPointerUp = (event: PointerEvent) => {
        if (event.pointerType !== 'touch') {
            return;
        }
        touches.delete(event.pointerId);
        if (multiTouch) {
            // The end of a pinch is not a tap.
            if (touches.size === 0) {
                multiTouch = false;
            }
            lastTap = null;
            return;
        }
        const isSecondTap =
            lastTap !== null &&
            event.timeStamp - lastTap.time < DOUBLE_TAP_MS &&
            Math.hypot(event.clientX - lastTap.x, event.clientY - lastTap.y) < DOUBLE_TAP_SLOP_PX;
        if (isSecondTap) {
            lastTap = null;
            toggleAt(event);
            return;
        }
        lastTap = { time: event.timeStamp, x: event.clientX, y: event.clientY };
    };
    // Touch double-taps are handled above; some browsers also report them as dblclick.
    const onDoubleClick = (event: MouseEvent) => {
        if (lastPointerType !== 'touch') {
            toggleAt(event);
        }
    };
    // A dragged <img> starts the browser's own drag and drop, which swallows the swipe.
    const onDragStart = (event: DragEvent) => event.preventDefault();

    stage.addEventListener('wheel', onWheel, { passive: false });
    node.addEventListener('pointerdown', onPointerDown);
    node.addEventListener('pointerup', onPointerUp);
    node.addEventListener('pointercancel', onPointerUp);
    node.addEventListener('dblclick', onDoubleClick);
    node.addEventListener('dragstart', onDragStart);

    return () => {
        stage.removeEventListener('wheel', onWheel);
        node.removeEventListener('pointerdown', onPointerDown);
        node.removeEventListener('pointerup', onPointerUp);
        node.removeEventListener('pointercancel', onPointerUp);
        node.removeEventListener('dblclick', onDoubleClick);
        node.removeEventListener('dragstart', onDragStart);
    };
}
